import React, { ReactNode } from "react";
import {
  mdiChevronLeft,
  mdiChevronRight,
  mdiChevronDoubleLeft,
  mdiChevronDoubleRight,
} from "@mdi/js";
import {
  cn,
  incPanelColorLevel,
  panelBackgroundStyles,
  panelBorderStyles,
} from "../../../styles";
import { Button, ButtonVariant } from "../../../atoms/Button";
import { Select, SelectOption } from "../../../molecules/Select";
import { useDataTableContext } from "../context";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../constants";
import { Typography } from "../../../atoms/Typography";

export interface FooterProps {
  actions?: ReactNode;
  className?: string;
}

export function Footer({ actions, className }: FooterProps) {
  const { table, color, dense, pagination } = useDataTableContext();

  const footerColor = incPanelColorLevel(color, 1);

  if (!pagination && !actions) {
    return null;
  }

  const page = pagination?.page ?? 0;
  const pageSize = pagination?.pageSize ?? 10;
  const pageSizeOptions =
    pagination?.pageSizeOptions ?? DEFAULT_PAGE_SIZE_OPTIONS;
  const totalCount = pagination.totalCount ?? table.totalCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  // Build options for page size select
  const pageSizeSelectOptions: SelectOption[] = pageSizeOptions.map((size) => ({
    value: size,
    label: String(size),
  }));

  const selectedPageSizeOption =
    pageSizeSelectOptions.find((o) => o.value === pageSize) ?? null;

  const handlePageSizeChange = (option: SelectOption | SelectOption[]) => {
    if (Array.isArray(option)) return;
    const newSize = option?.value as number;
    if (newSize && pagination) {
      pagination.setPageSize(newSize);
    }
  };

  return (
    <div
      className={cn(
        "flex items-center justify-between border-t",
        panelBackgroundStyles[footerColor],
        panelBorderStyles[color],
        dense ? "px-1.5 py-0.5" : "px-3 py-1",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-bold text-front-400 uppercase tracking-widest">
          {totalCount}<Typography.Span className="ml-1" local>Results</Typography.Span>
        </span>
        {actions}
      </div>

      {pagination && (
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-[9px] font-bold text-front-400 uppercase">
              <Typography.Span local>RowsPerPage</Typography.Span>
            </span>
            <Select
              options={pageSizeSelectOptions}
              value={selectedPageSizeOption}
              onChange={handlePageSizeChange}
              dense
              clearable={false}
              className="w-20"
            />
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant={ButtonVariant.text}
              disabled={page === 0}
              onClick={() => pagination.setPage(0)}
              iconPath={mdiChevronDoubleLeft}
              dense
            />
            <Button
              variant={ButtonVariant.text}
              disabled={page === 0}
              onClick={() => pagination.prev()}
              iconPath={mdiChevronLeft}
              dense
            />

            <span className="px-3 text-[11px] font-bold text-front-600">
              {page + 1} / {totalPages}
            </span>

            <Button
              variant={ButtonVariant.text}
              disabled={page >= totalPages - 1}
              onClick={() => pagination.next()}
              iconPath={mdiChevronRight}
              dense
            />
            <Button
              variant={ButtonVariant.text}
              disabled={page >= totalPages - 1}
              onClick={() => pagination.setPage(totalPages - 1)}
              iconPath={mdiChevronDoubleRight}
              dense
            />
          </div>
        </div>
      )}
    </div>
  );
}
